import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/shadcn/accordion";

const Heading = ({ children }: { children: string }) => (
  <p className="mt-4 mb-1 text-sm font-semibold first:mt-0">{children}</p>
);

const Body = ({ children }: { children: React.ReactNode }) => (
  <p className="my-1 text-sm text-muted-foreground">{children}</p>
);

const List = ({ children }: { children: React.ReactNode }) => (
  <ul className="my-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">{children}</ul>
);

/** Long-form help, collapsed by default so it doesn't dominate the page. */
export const MailSyncHelp = () => (
  <Accordion type="single" collapsible>
    <AccordionItem value="mail-sync" className="border-none">
      <AccordionTrigger className="py-0 text-sm font-semibold hover:no-underline">
        More detail about mail filing
      </AccordionTrigger>
      <AccordionContent className="pt-3 pb-0">
        <Heading>How it works</Heading>
        <Body>
          Once connected, Klipboard CRM reads your inbox and sent items and files relevant
          emails against the companies and contacts they involve. New emails usually appear
          within a few minutes.
        </Body>
        <Body>
          This is <strong>one way only</strong>, unlike the calendar. Emails are copied from
          Microsoft into Klipboard CRM. We never send, reply to, move, change or delete
          anything in your mailbox, and nothing you do in Klipboard CRM is written back to it.
        </Body>

        <Heading>Which emails are filed?</Heading>
        <Body>
          Only emails that involve someone you do business with. An email is filed when at
          least one of its addresses - sender, recipients or anyone copied in - matches a
          contact or a company in Klipboard CRM.
        </Body>
        <List>
          <li>
            <strong>Emails with no matching contact or company are ignored.</strong> That's what
            keeps newsletters, marketing and personal mail out of Klipboard CRM.
          </li>
          <li>
            <strong>Emails only between colleagues are ignored</strong>, even though your
            colleagues are Klipboard CRM users, because there's no customer to file them against.
          </li>
          <li>Both emails you receive and emails you send are filed.</li>
        </List>

        <Heading>An email I expected isn't here</Heading>
        <Body>Give it a few minutes first. If it still hasn't appeared:</Body>
        <List>
          <li>
            Check the email address is actually on the contact or company record. We match the
            whole address exactly, so mail from a personal or secondary address won't be
            recognised until you add it.
          </li>
          <li>Check the email arrived after you connected - older mail isn't imported.</li>
          <li>
            Check it isn't in Junk or another folder. We only read your inbox and sent items.
          </li>
        </List>

        <Heading>Why isn't my existing mailbox in Klipboard CRM?</Heading>
        <Body>
          Connecting doesn't import your mail history. We begin from when you connect. Nothing
          already in your mailbox is copied, moved or changed.
        </Body>

        <Heading>What can Klipboard CRM see?</Heading>
        <Body>
          We ask Microsoft for read-only access to your mail. Only emails that match a contact
          or company are stored in Klipboard CRM, and those are visible to colleagues who can
          see that customer record - so treat filed email as shared, not private.
        </Body>

        <Heading>Do I need to reconnect or renew anything?</Heading>
        <Body>
          No. The connection renews itself. If Microsoft stops accepting it - usually after a
          password change or when access is withdrawn - filing pauses and this page tells you
          to reconnect.
        </Body>

        <Heading>How do I stop it?</Heading>
        <Body>
          Disconnect at any time. New emails stop being filed immediately. Emails already in
          Klipboard CRM stay where they are, and your mailbox is untouched.
        </Body>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);
